// Single source of truth for the Septima Wealth ENTITY AML Assessment (SEP-186) — the
// field set, the Yes/No decision questions, the conditional business list and
// the risk banding. Lives in `@septima/contract` (the wire layer) for the same
// reason `aml-assessment-compute.ts` does: the web form, the server controller
// and any future PDF all need the identical field set.
//
// The sibling instrument is the personal (Contact-level) AML assessment. This
// one is anchored to the SUITABILITY RECORD, because it asks about the entity
// the mandate is held by — a company, a trust, a charity — and not about a
// person. Everything about it that CAN be shared with the personal instrument
// is imported from `aml-assessment-compute.ts` rather than copied: the
// cash-generative business list (`AML_HIGH_RISK_OCCUPATIONS`) and the
// Low / Medium / High banding.
//
// ─────────────────────────────────────────────────────────────────────────────
// SCORING IS NOT AGREED YET. Read `scoreEntityAml` before changing anything.
// ─────────────────────────────────────────────────────────────────────────────

import {
    AML_HIGH_RISK_OCCUPATIONS,
    amlRiskBand,
    amlRiskBandLabel,
    type AmlRiskBand,
} from './aml-assessment-compute';

/** The cards the Entity AML form groups its fields into. */
export type EntityAmlCategory = 'entity_identification' | 'high_risk_factors';

/** Render order of the category cards. */
export const ENTITY_AML_CATEGORY_ORDER: readonly EntityAmlCategory[] = [
    'entity_identification',
    'high_risk_factors',
] as const;

/**
 * Human label per category card. A switch rather than a keyed record: the
 * category values are snake_case DOMAIN VALUES, and an object literal would put
 * them in key position, which the snake-case guard (rightly) forbids.
 */
export function entityAmlCategoryLabel(category: EntityAmlCategory): string {
    switch (category) {
        case 'entity_identification':
            return 'Entity Identification';
        case 'high_risk_factors':
            return 'High Risk Factors';
    }
}

/**
 * How a field is collected.
 *
 * `yes_no` fields are DECISION questions — they carry the AML judgement and are
 * what the completion gate counts. `text`, `date` and `country` fields are
 * DESCRIPTIVE: they record what the entity is, and several of them are
 * legitimately blank (a Cayman company has no Companies House link), so they
 * are deliberately NOT part of the gate.
 */
export type EntityAmlFieldKind = 'text' | 'date' | 'country' | 'yes_no';

/** One selectable answer on a decision question. */
export interface EntityAmlOption {
    /** Stored answer value — a closed snake_case domain string. */
    readonly value: string;
    readonly label: string;
}

/**
 * The Yes/No pair, shared by every decision question.
 *
 * Note what is NOT here: a per-option `score`. The personal AML instrument
 * carries one because Compliance supplied weights for it; for the entity
 * instrument they have not, and inventing a weight per option would be
 * indistinguishable at the call site from a real one. See `scoreEntityAml`.
 */
export const ENTITY_AML_YES_NO_OPTIONS: readonly EntityAmlOption[] = [
    { value: 'no', label: 'No' },
    { value: 'yes', label: 'Yes' },
] as const;

/** One field on the Entity AML form. `key` is the stable camelCase answer key. */
export interface EntityAmlField {
    readonly key: string;
    readonly category: EntityAmlCategory;
    readonly prompt: string;
    readonly kind: EntityAmlFieldKind;
    /** Free-text placeholder, where the expected shape is not obvious. */
    readonly placeholder?: string;
}

/**
 * The business-source-of-income question key. When it is answered `'yes'` the
 * form reveals the SAME cash-generative business list the personal AML
 * instrument uses (`AML_HIGH_RISK_OCCUPATIONS`); the selection is stored in the
 * record's own `highRiskBusiness` field and is descriptive, never scored.
 */
export const ENTITY_AML_BUSINESS_QUESTION_KEY = 'businessSourceOfIncomeHighRisk';

/**
 * The conditional business list. Re-exported, NOT redeclared — the ticket asks
 * for "the same business dropdown as personal AML", so there is exactly one
 * list and both instruments read it.
 */
export const ENTITY_AML_HIGH_RISK_BUSINESSES: readonly string[] = AML_HIGH_RISK_OCCUPATIONS;

/** The Entity AML fields in render order. */
export const ENTITY_AML_FIELDS: readonly EntityAmlField[] = [
    {
        key: 'countryOfIncorporation',
        category: 'entity_identification',
        prompt: 'Country of Incorporation',
        kind: 'country',
    },
    {
        key: 'dateOfIncorporation',
        category: 'entity_identification',
        prompt: 'Date of Incorporation',
        kind: 'date',
    },
    {
        key: 'companiesHouseLink',
        category: 'entity_identification',
        prompt: 'Companies House Link (if UK)',
        kind: 'text',
        placeholder: 'https://find-and-update.company-information.service.gov.uk/…',
    },
    {
        key: 'natureOfBusiness',
        category: 'entity_identification',
        prompt: "Nature of Client's Business",
        kind: 'text',
    },
    {
        key: 'linkToAccounts',
        category: 'entity_identification',
        prompt: 'Link to Accounts',
        kind: 'text',
    },
    {
        key: 'boardApprovedOnLetterhead',
        category: 'entity_identification',
        prompt: 'Has the Board Approved Opening the Account on Letter Headed Paper?',
        kind: 'yes_no',
    },
    {
        key: 'applicantIdVerified',
        category: 'entity_identification',
        prompt: 'Has ID Been Verified for the Person Applying?',
        kind: 'yes_no',
    },
    {
        key: 'authorisedSignatories',
        category: 'entity_identification',
        prompt: 'List of names of Authorised Signatories from the Signatory List',
        kind: 'text',
    },
    {
        key: 'applicantIsAuthorisedSignatory',
        category: 'entity_identification',
        prompt: 'Is the Applicant an Authorised Signatory?',
        kind: 'yes_no',
    },
    {
        key: 'beneficialOwners',
        category: 'entity_identification',
        prompt: 'List of names of Beneficial Owners',
        kind: 'text',
    },
    {
        key: ENTITY_AML_BUSINESS_QUESTION_KEY,
        category: 'high_risk_factors',
        prompt: 'Is the business source of income considered high risk?',
        kind: 'yes_no',
    },
    {
        key: 'isTrust',
        category: 'high_risk_factors',
        prompt: 'Is this a trust?',
        kind: 'yes_no',
    },
    {
        key: 'officersOrBeneficialOwnersPep',
        category: 'high_risk_factors',
        prompt: "Have any of the Client's Officers/Beneficial Owners, their family or associates, been identified as PEPs?",
        kind: 'yes_no',
    },
    {
        key: 'negativeMediaInformation',
        category: 'high_risk_factors',
        prompt: 'Is there negative information from media searches?',
        kind: 'yes_no',
    },
    {
        key: 'beneficialOwnersMatchCompaniesHouse',
        category: 'high_risk_factors',
        prompt: 'Is the information regarding beneficial owners the same as shown on Companies House?',
        kind: 'yes_no',
    },
    {
        key: 'registeredAddressPoBox',
        category: 'high_risk_factors',
        prompt: 'Is the registered address a PO Box?',
        kind: 'yes_no',
    },
    {
        key: 'foreignBankAccount',
        category: 'high_risk_factors',
        prompt: 'Does the client have a foreign bank account?',
        kind: 'yes_no',
    },
    {
        key: 'bankAccountInClientName',
        category: 'high_risk_factors',
        prompt: 'Is the bank account in the name of the client?',
        kind: 'yes_no',
    },
] as const;

/** The decision questions — the subset the completion gate counts. */
export const ENTITY_AML_DECISION_FIELDS: readonly EntityAmlField[] = ENTITY_AML_FIELDS.filter(
    (f) => f.kind === 'yes_no',
);

/** The stored answers, keyed by field key. Unanswered / blank = absent. */
export type EntityAmlAnswers = Record<string, string | null | undefined>;

/**
 * THE ENTITY AML SCORE — a named, isolated placeholder rule, NOT the firm's
 * scoring.
 *
 * SEP-186 is explicit: *"just default the score to 1 (medium risk) for now and
 * we will ask compliance for the exact scoring."* So this returns 1 for every
 * record, and it is the ONLY place in the codebase that decides an entity AML
 * score — no call site anywhere writes the literal. When Compliance supplies
 * the real rule it lands inside this function and nothing else changes: the
 * score is a STORED column, already present, so there is no migration either.
 *
 * SETTLED with the business 2026-07-30: the ticket's default SHIPS. No
 * compliance answer is being waited on, and this is not a blocked placeholder —
 * it is the agreed behaviour until somebody asks for something else.
 *
 * The SEP-171 precedent is why the rule still lives in one function rather than
 * at its call sites: the personal AML instrument shipped on the same
 * "weights to follow" promise, and when they eventually changed the rework
 * landed on four surfaces at once, because assumptions about the bands had
 * leaked out of the compute module. Keep the assumption in here.
 *
 * Because the number is a placeholder, the surfaces that render it say so: the
 * band pill is withheld until the instrument is fully answered, exactly as the
 * personal instrument withholds its own.
 */
export const ENTITY_AML_PLACEHOLDER_SCORE = 1;

export function scoreEntityAml(_answers: EntityAmlAnswers): number {
    // The answers are deliberately not read yet — see the note above. The
    // parameter exists so the real rule drops in without touching a call site.
    return ENTITY_AML_PLACEHOLDER_SCORE;
}

export interface EntityAmlDerived {
    /** The rule's score — from `scoreEntityAml`, and STORED on the record. */
    score: number;
    /** Count of DECISION questions carrying a recognised answer. */
    answered: number;
    /** Number of decision questions on the form. */
    answerable: number;
    band: AmlRiskBand;
    /** "Low" / "Medium" / "High". */
    bandLabel: string;
}

/**
 * Whether every DECISION question carries a recognised answer.
 *
 * This is the COMPLETION GATE. The descriptive fields (country, dates, links,
 * the signatory and beneficial-owner name lists) are excluded on purpose: they
 * are frequently and legitimately blank, and requiring them would push IMs to
 * type "n/a" into an AML record. The Yes/No set is what the assessment
 * actually decides, and a half-answered one must never be signed off.
 */
export function isEntityAmlAssessmentFullyAnswered(
    derived: Pick<EntityAmlDerived, 'answered' | 'answerable'>,
): boolean {
    return derived.answerable > 0 && derived.answered >= derived.answerable;
}

/**
 * Compute the score, the decision-question progress and the risk band from the
 * stored answers. Unrecognised keys contribute nothing.
 */
export function computeEntityAmlAssessment(answers: EntityAmlAnswers): EntityAmlDerived {
    const given = answers ?? {};
    let answered = 0;
    for (const field of ENTITY_AML_DECISION_FIELDS) {
        const selected = given[field.key];
        if (ENTITY_AML_YES_NO_OPTIONS.some((o) => o.value === selected)) answered += 1;
    }

    const score = scoreEntityAml(given);
    const band = amlRiskBand(score);
    return {
        score,
        answered,
        answerable: ENTITY_AML_DECISION_FIELDS.length,
        band,
        bandLabel: amlRiskBandLabel(band),
    };
}
